/*
 * Copyright © 2023. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React, { useEffect, useState } from "react";
import { SERVICE_EMAIL } from "../../services/globalVars";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { apiGet } from "../../services/apiClient";
import LegalbirdLoader from "../ContentLoader/LegalbirdLoader";
import * as messagesActions from "../../store/messages/actions";
import ButtonLoading from "../Button/ButtonLoading";
import { useQuery } from "@tanstack/react-query";
import { fetchCollection, queryKeys } from "../../services/reactQuery/reactQueryService";
import { Box, Stack } from "@mui/material";
import ProductBox from "@/src/components/ProductBox/ProductBox";
import LinkLegalbird from "@/src/components/legalbird/LinkLegalbird/LinkLegalbird";
import { ArrowForward } from "@mui/icons-material";
import { accountDeleteStyle } from "@/src/components/MyAccount/accountDeleteStyle";

export default function AccountDelete() {
  const [isDeletable, setIsDeletable] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [deletionToken, setDeletionToken] = useState("");
  const [deletionEmailSent, setDeletionEmailSent] = useState(false);
  const [isRequestingDeletion, setIsRequestingDeletion] = useState(false);
  const { data: minimalCases, isLoading: isLoadingMinimalCases } = useQuery({
    queryKey: queryKeys.collection("/collection/cases"),

    queryFn: () => fetchCollection("/collection/cases"),
  });

  async function checkDeletable() {
    const result = await apiGet("/account_deletion/check_deletion_possible_from_token");
    setIsDeletable(result.deletable);
    setDeletionToken(result.token);
    setIsLoading(false);
  }

  async function requestDeletion() {
    setIsRequestingDeletion(true);
    try {
      await apiGet(`/account_deletion/request/${encodeURIComponent(deletionToken)}`);
      setDeletionEmailSent(true);
      messagesActions.addMessage({
        type: "success",
        text: "Wir haben Ihnen eine E-Mail zur Bestätigung Ihrer Kontolöschung geschickt.",
      });
    } catch (e) {
      messagesActions.addMessage({
        type: "error",
        text: "Es ist ein Fehler beim Verschicken der E-Mail aufgetreten!",
      });
    } finally {
      setIsRequestingDeletion(false);
    }
  }

  useEffect(() => {
    checkDeletable();
  }, [minimalCases]);

  return (
    <ProductBox headline={"Konto löschen"}>
      <Grid container spacing={4}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack sx={accountDeleteStyle.firstCol}>
            {(isLoading || isLoadingMinimalCases) && (
              <Box sx={{ marginTop: "1rem" }}>
                <LegalbirdLoader />
              </Box>
            )}
            {isDeletable === false && minimalCases && minimalCases.length === 0 && (
              <>
                Aufgrund von berufsstandesrechtlichen Aufbewahrungspflichten eines Rechtsanwalts können Ihre Daten leider nicht automatisch gelöscht werden!
                Bitte schicken Sie eine E-Mail an
                <a href={"mailto:" + SERVICE_EMAIL}> {SERVICE_EMAIL}</a> mit Ihrer Löschanfrage, damit wir diese prüfen und zeitnah weiter bearbeiten können.
              </>
            )}
            {isDeletable === false && minimalCases && minimalCases.length > 0 && (
              <>
                <Typography>
                  Aufgrund von berufsstandesrechtlichen Aufbewahrungspflichten eines Rechtsanwalts können Ihre Daten leider nicht automatisch gelöscht werden!
                  Bitte schicken Sie uns eine Löschanfrage über Ihren Nachrichtenbereich, damit wir diese prüfen und zeitnah weiter bearbeiten können.
                </Typography>
                <Stack sx={accountDeleteStyle.linkWrapper}>
                  <LinkLegalbird to={"/servicewelt/nachrichten"} standalone sx={accountDeleteStyle.link}>
                    Zum Nachrichtenbereich <ArrowForward />
                  </LinkLegalbird>
                </Stack>
              </>
            )}
            {isDeletable === true && (
              <>
                <Typography>
                  Bitte klicken Sie hier, um eine Löschung ihres Kontos zu beantragen. Sie erhalten dann eine E-Mail mit einem Bestätigungslink.
                </Typography>
                <Stack sx={{ flexGrow: 1, justifyContent: "flex-end", alignItems: "start", marginTop: "1rem" }}>
                  <ButtonLoading
                    isLoading={isRequestingDeletion}
                    variant={"outlined"}
                    color={"warning"}
                    disabled={deletionEmailSent}
                    onClick={() => requestDeletion()}
                    sx={{ width: { xs: "100%", md: "16rem" } }}
                  >
                    {deletionEmailSent ? "E-Mail wurde versendet" : "Löschung beantragen"}
                  </ButtonLoading>
                </Stack>
              </>
            )}
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack sx={{ height: "100%" }}>
            <Typography>
              Sie möchten nicht das ganze Konto, sondern nur einen einzelnen Fall löschen, weil sie diesen nicht (mehr) benötigen? Dann können Sie dies ganz
              einfach über den jeweiligen Fall tun. Wählen Sie hierzu in der Fall-Übersicht den entsprechenden Fall aus und klicken Sie dort auf “Fall löschen”.
              Sie finden die Funktion zum Löschen des Falls nicht? Dann kann der Fall vermutlich aufgrund anwaltlicher Aufbewahrungspflichten nicht automatisch
              gelöscht werden. Bitte schicken Sie uns dann eine Löschanfrage über Ihren Nachrichtenbereich, damit wir die Löschung prüfen und zeitnah bearbeiten
              können.
            </Typography>
            <Stack sx={accountDeleteStyle.linkWrapper}>
              <LinkLegalbird to={"/servicewelt"} standalone sx={accountDeleteStyle.link}>
                Zur Fall-Übersicht <ArrowForward />
              </LinkLegalbird>
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    </ProductBox>
  );
}
